import axios from 'axios';
import { Auth, DataStore } from 'aws-amplify';
import { Member, MemberType, PointHistory, RegistrationCode } from '../models/index'
import { CardListDto, CardPaymentDto, RegistCardDto, RegistCustomerDto, SendCodeMailDto } from '../../../common/card.dto'
import dayjs from 'dayjs';
import { string } from 'yup';
import CryptoJS from 'crypto-js';


const api = axios.create();
api.defaults.baseURL = process.env.API_URL;

const apiDevice = axios.create();
apiDevice.defaults.baseURL = process.env.API_DEVICE_URL;

const Api = {
  // Cognitoのトークンをセット
  setCognitoToken: function(token: string) {
    apiDevice.defaults.headers.common['Authorization'] = 'Bearer ' + token;
  },
  // メール送信
  sendMail : async function(data: SendCodeMailDto) {
    const res = await api.post('/api/mail-code', {
      headers: {
        'Content-Type': 'application/json',
      },
      data
    });
    console.log(res);
    if (!res) {
      return null;
    }
    return res.data;
  },
  // メンバー取得
  getMember: async function(memberId: string) {
    const buf = await DataStore.query(Member, (m) => m.id('eq', memberId));
    if (!buf || buf.length <= 0) {
      return null;
    }
    return buf[0];
  },
  // emailからメンバー取得
  getMemberByEmail: async function(email: string) {
    const buf = await DataStore.query(Member, (m) => m.email('eq', email));
    if (!buf || buf.length <= 0) {
      return null;
    }
    return buf[0];
  },
  // トークンからセッション取得
  getMachineSessionByToken: async function(token: string) {
    const res = await api.get('/api/session?token=' + token, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    console.log(res);
    if (!res) {
      return null;
    }
    return res.data;
  },
  // トークンを更新
  updateMachineSession: async function(token: string, memberId: string, cogToken: string) {
    const res = await api.post('/api/session', {
      headers: {
        'Content-Type': 'application/json',
      },
      info: {
        token,
        memberId,
        cogToken
      }
    });
    console.log(res);
    if (!res) {
      return null;
    }
    return res.data;
  },
  // カード会員登録
  registCardCustomer: async function(info: RegistCustomerDto) {
    const res = await api.post('/api/customer', {
      headers: {
        'Content-Type': 'application/json',
      },
      info,
    });
    console.log(res);
    if (!res) {
      return null;
    }
    return res.data;
  },
  // カード一覧取得
  getCards: async function(customerId: string, encryptedKeys: string): Promise<CardListDto[]> {
    const res = await api.get('/api/card?customerId='+customerId+'&encryptedKeys='+encryptedKeys, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    if (!res) {
      return [];
    }
    return res.data;
  },
  // カード登録
  registCard: async function(info: RegistCardDto) {
    const res = await api.post('/api/card', {
      headers: {
        'Content-Type': 'application/json',
      },
      info,
    });
    if (!res) {
      return null;
    }
    return res.data;
  },
  // カード削除
  deleteCard: async function(cardId: string, encryptedKeys: string) {
    const res = await api.delete('/api/card?cardId='+cardId+'&encryptedKeys='+encryptedKeys, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    if (!res) {
      return null;
    }
    return res.data;
  },
  // ポイント購入 カード決済
  buyPoint: async function(info: CardPaymentDto) {
    const res = await api.post('/api/payment', {
      headers: {
        'Content-Type': 'application/json',
      },
      info,
    });
    if (!res) {
      return null;
    }
    return res.data;
  },
  // 登録コード取得
  getRegistrationCode: async function(email: string) {

    await DataStore.delete(RegistrationCode, (c) => c.email('eq', email));

    let code = 0;

    const expiredAt = dayjs().add(30, 'm').format('YYYY-MM-DD HH:mm:ss');
    const now =  dayjs().format('YYYY-MM-DD HH:mm:ss');

    for(let n=0; n<=5; n++) {
      if (n >= 5) {
        return null;
      }
      let text = ""
      const chars = "123456789"
    
      for(let i=0; i < 6; i++ ) {
        text += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      code = parseInt(text);

      const check = await DataStore.query(RegistrationCode, (r) => r.code('eq', code).expiredAt('gt', now));

      if (check.length <= 0) {
        break;
      }
    }

    const entity = new RegistrationCode({
      email,
      code,
      used: false,
      expiredAt,
    });
    const res = await DataStore.save(entity);

    return code;
  },
  // 登録コードチェック
  verifyRegistrationCode: async function(code: string) {
    const now =  dayjs().format('YYYY-MM-DD HH:mm:ss');
    const reg = await DataStore.query(RegistrationCode, (r) => r.code('eq', parseInt(code)).expiredAt('gt', now));
    if (reg.length <= 0) {
      return null;
    }

    return reg[0];
  },
  // 解錠
  sendUnlockToDevice: async function(deviceCode: string, serialNo: number) {
    const res = await apiDevice.post(`/toDevice`, 
      {
        deviceId: deviceCode,
        serialNo: 0, // 0固定値
        data: serialNo * 10 // 鍵番号を10倍する
      });
    return res.data;
  },
  // 暗号化用のキーを取得
  generateEncryptedKeys: async function(keys: string) {

    const cryptoKey: any = process.env.SQUARE_CRYPTO_KEY;
    const utf8_plain = CryptoJS.enc.Utf8.parse(keys);
    const encrypted = CryptoJS.AES.encrypt( utf8_plain, cryptoKey);
    return  CryptoJS.enc.Base64.stringify(CryptoJS.enc.Latin1.parse(encrypted.toString()));
  },
  // 月会費を支払う
  paymentMonthly: async function(member: Member, price: number, newType: MemberType, newExpir: string) {
    const newMember = await DataStore.save(
      Member.copyOf(member , updated => {
        updated.point = member.point! - price;
        updated.memberType = newType;
        updated.memberExpire = newExpir;
      })
    );

    await DataStore.save( new PointHistory({
      usageType : 0,
      paymentMethod : 0,
      memo : 'Use point to update memberType' + price,
      addPoint : 0,
      usePoint : price,
      totalPoint : newMember.point,
      memberID : member.id
    }));
  }
}

export {Api};
